/**
 * The install steps and the install-time claim. Every install surface on the
 * site renders from this module (ANTI_SLOP.md rule 4: one step count, order
 * and wording): the condensed three-step form on post-download and hub
 * surfaces, the expanded four-step form on /guide and the SEO pages, and the
 * HowTo JSON-LD for whichever form a page shows.
 *
 * If you reword a step here, every consumer updates together. Do not add a
 * competing hand-written step list to a component;
 * lib/__tests__/install-surfaces.test.ts fails on one.
 */

import { TESLA_FACTS } from "@/lib/tesla-facts";

const { filename, path, folder, formats, portNote } = TESLA_FACTS.usb;
const { finalStep } = TESLA_FACTS.menu;
const { sampleRate, bitDepth } = TESLA_FACTS.audio.recommended;

/** The one install-time claim, in prose: "2 minutes". */
export const INSTALL_TIME_LABEL = TESLA_FACTS.install.label;
/** The same claim where space is short (the nav CTA): "2 min". */
export const INSTALL_TIME_SHORT = `${TESLA_FACTS.install.minutes} min`;
/** ISO-8601 duration for HowTo structured data. */
export const INSTALL_TIME_ISO = TESLA_FACTS.install.iso;

export interface CondensedInstallStep {
  /** Short imperative title (wizard rows, headings). */
  title: string;
  /** Full one-sentence instruction (receipt lines, toast list items). */
  body: string;
}

export const CONDENSED_INSTALL_STEPS: readonly CondensedInstallStep[] = [
  {
    title: "Copy to USB",
    body: `Copy ${filename} onto a FAT32 or exFAT USB drive at ${path}.`,
  },
  {
    title: "Plug into your Tesla",
    body: `Plug the drive into a USB port that carries data (the glovebox port on newer cars). Open ${finalStep}.`,
  },
  {
    title: "Lock the car",
    body: "Lock the car to hear it.",
  },
] as const;

export interface ExpandedInstallStep {
  /** Short imperative title (card headings). */
  title: string;
  /** Longer instruction for SEO-page install sections. */
  description: string;
}

/**
 * Canonical EXPANDED install steps for SEO pages (horn-sounds, lockchime-wav,
 * tesla-unlock-sound, boombox-sounds). Same facts, longer form. Do not add a
 * competing hand-written step list to a page.
 */
export const EXPANDED_INSTALL_STEPS: readonly ExpandedInstallStep[] = [
  {
    title: `Download ${filename}`,
    description: `Pick a sound and download it. Every file arrives named ${filename} in ${sampleRate}, ${bitDepth} WAV, the recommended spec for clean playback. The only hard Tesla requirement is a WAV file named ${filename}.`,
  },
  {
    title: `Copy it into a ${folder} folder`,
    description: `On a ${formats[0]} or ${formats[1]} USB drive, create a folder named ${folder} and place ${filename} inside it. Full path: ${path}.`,
  },
  {
    title: "Plug into a USB port that carries data",
    description: `Insert the drive into ${portNote}. Give the car a few seconds to index the drive.`,
  },
  {
    title: "Switch the source to USB",
    description: `Open ${finalStep}. Lock the car to hear it.`,
  },
] as const;

export type InstallStepsVariant = "condensed" | "expanded";

export interface InstallStep {
  title: string;
  detail: string;
}

/** Both forms in one shape, for the InstallSteps component and HowTo JSON-LD. */
export const INSTALL_STEPS: Record<InstallStepsVariant, readonly InstallStep[]> = {
  condensed: CONDENSED_INSTALL_STEPS.map((step) => ({ title: step.title, detail: step.body })),
  expanded: EXPANDED_INSTALL_STEPS.map((step) => ({ title: step.title, detail: step.description })),
};

/** HowTo `step` entries matching what `<InstallSteps variant={variant} />` shows. */
export function installHowToSteps(variant: InstallStepsVariant) {
  return INSTALL_STEPS[variant].map((step, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: step.title,
    text: step.detail,
  }));
}
